export type SeoEnricherFaq = {
  question: string;
  answer: string;
};

export type SeoEnricherSection = {
  intent?: string;
  title: string;
  subtitle: string;
  content: string;
  faqs: SeoEnricherFaq[];
};

type RawSeoEnricherSection = Partial<SeoEnricherSection> & {
  sectionType?: string;
};

export const SEO_ENRICHER_PREVIEW_PARAM = 'seoEnricherPreviewContent';
export const OVERRIDE_GENERATED_CONTENT_PARAM = 'overrideGeneratedContent';

export type TokenValues = Record<
  string,
  string | number | boolean | null | undefined
>;

const toStringValue = (value: string | number | boolean | null | undefined) =>
  value === null || value === undefined ? '' : String(value);

const replaceTokens = (value: string, tokens: TokenValues): string => {
  if (!value) return value;
  return value.replace(/\$\{(\w+)\}/g, (match, key) =>
    Object.prototype.hasOwnProperty.call(tokens, key)
      ? toStringValue(tokens[key])
      : match
  );
};

const normalizeSection = (raw: RawSeoEnricherSection): SeoEnricherSection => ({
  intent: raw.intent || raw.sectionType || undefined,
  title: raw.title || '',
  subtitle: raw.subtitle || '',
  content: raw.content || '',
  faqs: Array.isArray(raw.faqs)
    ? raw.faqs.map(faq => ({
        question: faq.question || '',
        answer: faq.answer || ''
      }))
    : []
});

/**
 * Substitutes `${token}` placeholders (e.g. ${source_city_name}) in section
 * fields. Used for URL preview content from `seoEnricherPreviewContent`.
 */
export const applyTokensToSections = (
  sections: SeoEnricherSection[],
  tokens: TokenValues
): SeoEnricherSection[] =>
  sections.map(section => ({
    intent: section.intent,
    title: replaceTokens(section.title, tokens),
    subtitle: replaceTokens(section.subtitle, tokens),
    content: replaceTokens(section.content, tokens),
    faqs: section.faqs.map(faq => ({
      question: replaceTokens(faq.question, tokens),
      answer: replaceTokens(faq.answer, tokens)
    }))
  }));

/**
 * Parses the `seoEnricherPreviewContent` query param into typed sections
 * without token substitution. Returns an empty array if missing/unparseable.
 */
export const parseSeoEnricherPreviewContent = (
  search: string
): SeoEnricherSection[] => {
  try {
    const params = new URLSearchParams(search);
    const raw = params.get(SEO_ENRICHER_PREVIEW_PARAM);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(section => normalizeSection(section));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to parse seoEnricherPreviewContent', e);
    return [];
  }
};

/**
 * Resolves which sections to render:
 * - `seoEnricherPreviewContent` present → URL preview with token replacement
 * - otherwise → route data `generatedSections` as-is (no token processing)
 */
export const resolveSeoEnricherSections = (
  search: string,
  generatedSections: SeoEnricherSection[] | null | undefined,
  tokens: TokenValues = {}
): SeoEnricherSection[] => {
  const previewSections = parseSeoEnricherPreviewContent(search);

  if (previewSections.length) {
    return applyTokensToSections(previewSections, tokens);
  }

  if (!Array.isArray(generatedSections) || !generatedSections.length) {
    return [];
  }

  return generatedSections.map(section => normalizeSection(section));
};

/** Resolves city-page sections with `${city_name}` token substitution. */
export const resolveCitySeoEnricherSections = (
  search: string,
  sections: SeoEnricherSection[] | null | undefined,
  cityName: string
): SeoEnricherSection[] => {
  const tokens = { city_name: cityName || '' };
  const resolved = resolveSeoEnricherSections(search, sections, tokens);
  return applyTokensToSections(resolved, tokens);
};

/** Flattens FAQs from all resolved SEO enricher sections. */
export const flattenSeoEnricherFaqs = (
  sections: SeoEnricherSection[]
): SeoEnricherFaq[] =>
  sections.reduce<SeoEnricherFaq[]>((acc, section) => {
    if (Array.isArray(section.faqs) && section.faqs.length) {
      acc.push(...section.faqs);
    }
    return acc;
  }, []);
